import { useMutation } from "@tanstack/react-query";

import toast from "components/partials/toast/toast";

import { getClientConfiguration } from "generated/api/client-configuration";
import type { BeeConfigsRequest, JsonNode } from "generated/schemas";

import type { BeeConfigExtended, BrandStyleData } from "types/bee";

const clientConfigurationApi = getClientConfiguration();

/**
 * Fetches the published BEE editor config for a client. Backed by the generic
 * configuration GET (`get2`), which the backend serves as untyped `JsonNode`.
 */
export async function fetchBeeConfig(clientId: string): Promise<BeeConfigExtended> {
  const data = await clientConfigurationApi.get2({ clientId, config: "bee-config" });
  return data as unknown as BeeConfigExtended;
}

/**
 * Atomically publishes the BEE config and brand-style config for a client.
 * A single call keeps both configs in sync with no race condition. Shows a
 * success toast.
 */
export async function publishBeeConfigs(
  clientId: string,
  beeConfig: BeeConfigExtended,
  brandStyleConfig: BrandStyleData,
): Promise<void> {
  const body: BeeConfigsRequest = {
    beeConfig: beeConfig as unknown as JsonNode,
    brandStyleConfig: brandStyleConfig as unknown as JsonNode,
  };
  await clientConfigurationApi.createOrUpdateBeeConfigs({ clientId }, body);
  toast.success({ data: { title: "Published theme" } });
}

export const usePublishBeeConfigs = (clientId: string) =>
  useMutation({
    mutationFn: ({ beeConfig, brandStyleConfig }: { beeConfig: BeeConfigExtended; brandStyleConfig: BrandStyleData }) =>
      publishBeeConfigs(clientId, beeConfig, brandStyleConfig),
  });
